import {
  ChangeEvent,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  FieldValues,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { isValid } from "date-fns";

import TimeInput from "./time-input.component";
import TimePickerDropdown from "./time-picker-dropdown.component";
import {
  AM_PM,
  HOURS,
  MINUTES,
  dateToParts,
  formatDigit,
  timeStringToDate
} from "./time-picker.utils";

type TimePickerProps = {
  sx?: SxProps;
  onChange?: (value: Date) => void;
  onClose?: () => void;
  openPicker: boolean;
  showPicker?: () => void;
  hidePicker?: (_event?: MouseEvent | TouchEvent) => void;
  hideAllPickers?: () => void;
  disabled?: boolean;
  label?: string;
  minTime?: Date | null;
  maxTime?: Date | null;
};

export type RhfTimePickerFieldProps<T extends FieldValues> = TimePickerProps &
  UseControllerProps<T>;

const toH24 = (h12: string, ap: string) => {
  const h = Number(h12);
  return ap === "am" ? (h === 12 ? 0 : h) : h === 12 ? 12 : h + 12;
};

export default function TimePicker<T extends FieldValues>({
  control,
  name,
  onClose,
  openPicker,
  showPicker,
  hidePicker,
  disabled = false,
  label,
  sx,
  minTime,
  maxTime
}: RhfTimePickerFieldProps<T>) {
  const value = useWatch({ control, name: name as any });
  const {
    field: { onBlur, onChange, ...field },
    fieldState
  } = useController<T>({
    name: name as any,
    control,
    rules: {
      required: "Time is required"
    }
  });

  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [amPm, setAmPm] = useState("");

  // Current display parts, mirrored into refs so the value->parts sync effect can compare
  // against them without taking them as dependencies (which would re-run it on user edits).
  const hourRef = useRef(hour);
  const minuteRef = useRef(minute);
  const amPmRef = useRef(amPm);
  hourRef.current = hour;
  minuteRef.current = minute;
  amPmRef.current = amPm;

  const allowedAmPm = useMemo(() => {
    if (!minTime || !maxTime) return AM_PM;
    const minAm = minTime.getHours() < 12;
    const maxAm = maxTime.getHours() < 12;
    if (minAm === maxAm) return [minAm ? "am" : "pm"];
    return AM_PM;
  }, [minTime, maxTime]);

  const allowedHours = useMemo(() => {
    if (!minTime || !maxTime) return HOURS;
    const minH = minTime.getHours();
    const maxH = maxTime.getHours();
    // When amPm is not yet chosen, check validity against all allowed amPm options
    // so we never hide hours that are actually reachable. Without this, an empty
    // amPm is treated as "pm" by toH24, which hides all AM hours for morning slots.
    const aps = amPm ? [amPm] : allowedAmPm;
    return HOURS.filter((h12) =>
      aps.some((ap) => {
        const h24 = toH24(h12, ap);
        return h24 >= minH && h24 <= maxH;
      })
    );
  }, [minTime, maxTime, amPm, allowedAmPm]);

  const allowedMinutes = useMemo(() => {
    if (!minTime || !maxTime) return MINUTES;
    // Defer minute filtering until both hour and amPm are selected; otherwise the
    // toH24 call with an empty amPm produces a wrong 24-hour value and hides minutes.
    if (!hour || !amPm) return MINUTES;
    const minH = minTime.getHours();
    const maxH = maxTime.getHours();
    const curH24 = toH24(hour, amPm);
    return MINUTES.filter((m) => {
      const mNum = Number(m);
      if (curH24 === minH && curH24 === maxH) {
        return mNum >= minTime.getMinutes() && mNum <= maxTime.getMinutes();
      }
      if (curH24 === minH) return mNum >= minTime.getMinutes();
      if (curH24 === maxH) return mNum <= maxTime.getMinutes();
      return true;
    });
  }, [minTime, maxTime, hour, amPm]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  // True while the hour/minute/amPm below are being synced FROM `value`. Guards the
  // propagation effect so a value-driven part update is not pushed straight back out as
  // onChange — otherwise an externally-set value (e.g. the room clamp) and this picker
  // ping-pong the field forever (SN-17738 render loop).
  const syncingFromValueRef = useRef(false);

  useEffect(() => {
    if (value && isValid(value)) {
      const parsed = dateToParts(value, "", "", "");
      // Only sync (and raise the guard flag) when the display parts actually change. A no-op
      // sync would leave syncingFromValueRef stuck true — because the propagation effect below
      // never re-runs to clear it — and then swallow the user's NEXT edit (e.g. moving the
      // appointment FROM back after moving it forward). SN-17738.
      if (
        parsed.hour !== hourRef.current ||
        parsed.minute !== minuteRef.current ||
        parsed.amPm !== amPmRef.current
      ) {
        syncingFromValueRef.current = true;
        setHour(parsed.hour);
        setMinute(parsed.minute);
        setAmPm(parsed.amPm);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.getTime?.()]);

  useEffect(() => {
    if (hour === "" || minute === "" || amPm === "") return;
    if (syncingFromValueRef.current) {
      syncingFromValueRef.current = false;
      return;
    }
    const newDate = timeStringToDate(value, hour, minute, amPm);
    if (isValid(newDate) && newDate.getTime() !== value?.getTime()) {
      onChangeRef.current(newDate);
      onBlurRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hour, minute, amPm]);

  const handleHours = (value: string) => {
    setHour(value);
  };

  const handleMinutes = (value: string) => {
    setMinute(value);
  };

  const handleAmPm = (value: string) => {
    setAmPm(value);
  };

  const handleClose = () => {
    hidePicker?.();
    onClose?.();
  };

  // Wraps BOTH the text field and the dropdown, so click-away can tell "outside the whole
  // control" from "on my own field".
  const rootRef = useRef<HTMLDivElement>(null);

  // The dropdown's ClickAwayListener only knows about the Paper, so a click on the text field
  // reads as a click-away. That closes the picker on the very click that opened it: the browser
  // focuses the input on MOUSEDOWN, `onFocus` opens the dropdown, and by the time `click` is
  // dispatched (~100ms later) the freshly-mounted listener has already self-activated on its
  // `setTimeout(…, 0)` guard. The user sees the columns flash and has to click a second time.
  // Treat the field as part of the picker — clicking it should never dismiss the dropdown.
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (rootRef.current?.contains(event.target as Node)) return;
    hidePicker?.(event);
  };

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const timeString = event.target.value.trim();
    // Regular expression to match time format (HH:mm a)
    const match = timeString.match(/^(\d{1,2}):(\d{2})\s?(am|pm)?$/i);

    if (match) {
      const [, newHour, newMinute, newAmPm] = match;
      // Update state only if the format is correct
      setHour(formatDigit(newHour));
      setMinute(formatDigit(newMinute));
      if (newAmPm) {
        setAmPm(newAmPm.toLowerCase());
      }

      // Update the picker and field value if the time is valid
      const newDate = timeStringToDate(
        field.value,
        newHour,
        newMinute,
        newAmPm || amPm
      );
      if (isValid(newDate)) {
        onChange(newDate);
      }
    } else {
      if (timeString === "") {
        setHour("");
        setMinute("");
        setAmPm("");
        onChange(null);
      }
    }
  };

  return (
    <Stack sx={sx} ref={rootRef}>
      <TextField
        {...field}
        value={`${hour}:${minute} ${amPm}`}
        placeholder="hh:mm a"
        InputProps={{
          inputComponent: TimeInput
        }}
        onBlur={onBlur}
        onFocus={() => {
          if (disabled) return;
          startTransition(() => {
            showPicker?.();
          });
        }}
        label={label}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          startTransition(() => {
            showPicker?.();
          });
        }}
        error={Boolean(fieldState.error)}
        helperText={fieldState.error?.message}
        onChange={handleTextFieldChange}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleClose();
            event.preventDefault();
          }
        }}
      />
      {openPicker && (
        <TimePickerDropdown
          hour={hour}
          minute={minute}
          amPm={amPm}
          hours={allowedHours}
          minutes={allowedMinutes}
          amPms={allowedAmPm}
          onHour={handleHours}
          onMinute={handleMinutes}
          onAmPm={handleAmPm}
          onClose={handleClose}
          onClickAway={handleClickAway}
          hasLabel={Boolean(label)}
        />
      )}
    </Stack>
  );
}
